import { useAutoTranslation, useUserStore } from "@/entities"
import { DISCOVERED_TRANSLATION_KEYS } from "@/shared/constants"
import {
  authControllerUpdateUserEmail,
  userControllerProvideNewEmail,
  userControllerProvideStartChangeEmailCode,
  userControllerStartChangeEmail,
} from "@/shared/api/generated"
import { useState } from "react"
import { useForm } from "react-hook-form"

export function useRequestEmailCode(email: string) {
  const { t } = useAutoTranslation()
  const [codeSending, setCodeSending] = useState<boolean>(false)
  const [codeSent, setCodeSent] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<{ email: string }>({
    defaultValues: {
      email,
    },
  })

  const handleChangeEmail = async () => {
    try {
      setCodeSending(true)
      await userControllerStartChangeEmail()
      setCodeSending(false)
      setCodeSent(true)
    } finally {
      //empty
      setCodeSending(false)
    }
  }

  const onSubmit = handleSubmit(async () => {
    const callAlert = () => {
      const isSure = confirm(
        t(
          DISCOVERED_TRANSLATION_KEYS.ARE_YOU_SURE_YOU_WANT_TO_CHANGE_YOUR_EMAIL
        )
      )
      if (!isSure) return
      handleChangeEmail()
    }

    callAlert()
  })

  return {
    control,
    handleSubmit: onSubmit,
    errors,
    codeSending,
    codeSent,
    setValue,
    setCodeSent,
    resetRequestEmailCode: reset,
  }
}

export function useConfirmCode(handleRes: (code: string) => void) {
  const [confirmed, setConfirmed] = useState<boolean>(false)
  const [isLoading, setLoading] = useState(false)
  const [codeWrong, setCodeWrong] = useState("")

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<{ code: string }>({
    defaultValues: {
      code: "",
    },
  })

  const onSubmit = handleSubmit(async ({ code }) => {
    try {
      setLoading(true)
      setCodeWrong("")

      const response = await userControllerProvideStartChangeEmailCode({
        code,
      })
      handleRes(code)
      setLoading(false)

      if (response.isRight) {
        setConfirmed(true)
      } else {
        setCodeWrong(
          "An invalid value was entered. Check that the confirmation code is entered correctly and try again."
        )
      }
    } finally {
      //empty
      setLoading(false)
    }
  })

  return {
    control,
    handleSubmit: onSubmit,
    errors,
    confirmed,
    isLoading,
    codeWrong,
    setValue,
    setConfirmed,
    setCodeWrong,
    resetConfirmFirstCode: reset,
  }
}

export function useNewEmail(code: string) {
  const [isLoading, setLoading] = useState(false)
  const [newEmailProvided, setEmailProvided] = useState(false)
  const [selectedEmail, setSelectedEmail] = useState("")

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ email: string }>({
    defaultValues: {
      email: "",
    },
  })

  const onSubmit = handleSubmit(async ({ email }) => {
    try {
      setLoading(true)
      await userControllerProvideNewEmail({ email, code })
      setEmailProvided(true)
      setSelectedEmail(email)
      setLoading(false)
    } finally {
      //empty
      setLoading(false)
    }
  })

  return {
    isLoading,
    control,
    handleSubmit: onSubmit,
    errors,
    newEmailProvided,
    setEmailProvided,
    selectedEmail,
    setSelectedEmail,
    resetNewEmail: reset,
  }
}

export function useFinalCode() {
  const [done, setDone] = useState<boolean>(false)
  const [isLoading, setLoading] = useState(false)
  const [codeWrong, setCodeWrong] = useState("")
  const login = useUserStore(s => s.login)

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<{ code: string }>({
    defaultValues: {
      code: "",
    },
  })

  const onSubmit = handleSubmit(async ({ code }) => {
    try {
      setLoading(true)
      setCodeWrong("")
      const response = await authControllerUpdateUserEmail({ code })
      if (response.isRight && response.access_token) {
        const data = await login(response.access_token)
        setLoading(false)
        setDone(true)
        reset()

        alert(
          "Your email was updated\nNow use you new email to login" +
            "\n" +
            data.email
        )
      } else {
        setCodeWrong(
          "An invalid value was entered. Check that the confirmation code is entered correctly and try again."
        )
        setLoading(false)
      }
    } finally {
      //empty
      setLoading(false)
    }
  })

  return {
    control,
    handleSubmit: onSubmit,
    errors,
    isLoading,
    codeWrong,
    setValue,
    setCodeWrong,
    done,
    setDone,
    resetFinalCode: reset,
  }
}
